import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Trash2, Pencil, Send, FileText } from "lucide-react";

export default function History() {
  const [tab, setTab] = useState("preview"); // preview | sent
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ preview: 0, sent: 0 });
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: previews }, { data: sent }] = await Promise.all([
        api.get("/emails", { params: { status: "preview" } }),
        api.get("/emails", { params: { status: "sent" } }),
      ]);
      setCounts({ preview: previews.items?.length || 0, sent: sent.items?.length || 0 });
      setItems(tab === "preview" ? previews.items : sent.items);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this preview?")) return;
    try {
      await api.delete(`/emails/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const handleContinue = (item) => {
    sessionStorage.setItem("aplica_draft", JSON.stringify({
      id: item.id, subject: item.subject, body: item.body,
      to_email: item.to_email || "", detected_email: item.detected_email || "",
      jd_snippet: item.jd_snippet || "",
    }));
    navigate("/generate");
  };

  return (
    <AppShell>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="label-xs">Email history</span>
          <h1 className="mt-2 text-3xl md:text-4xl font-display tracking-tighter">All your outreach.</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex items-center gap-1 border-b border-slate-200" data-testid="history-tabs">
        <Tab active={tab === "preview"} onClick={() => setTab("preview")} testId="tab-preview"
          icon={FileText} label="Previews" count={counts.preview} />
        <Tab active={tab === "sent"} onClick={() => setTab("sent")} testId="tab-sent"
          icon={Send} label="Sent" count={counts.sent} />
      </div>

      <div className="mt-6 tech-card overflow-hidden" data-testid={`history-list-${tab}`}>
        {loading ? (
          <div className="p-12 text-center text-slate-400 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Mail className="h-8 w-8 mx-auto text-slate-300" />
            <p className="mt-3">
              {tab === "preview" ? "No previews yet. Generate an email and it'll show up here." : "No emails sent yet."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((e) => (
              <li key={e.id} className="hover:bg-slate-50 transition-colors">
                <div className="p-6 flex items-start gap-4 cursor-pointer"
                  onClick={() => setExpanded(expanded === e.id ? null : e.id)}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {tab === "preview" ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-800 font-semibold">
                          <FileText className="h-2.5 w-2.5" /> Draft
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-800 font-semibold">
                          <Send className="h-2.5 w-2.5" /> Sent
                        </span>
                      )}
                      <div className="font-mono text-xs text-slate-500">{e.to_email || (tab === "preview" ? "no recipient yet" : "—")}</div>
                    </div>
                    <div className="mt-1.5 font-display font-medium text-slate-900 truncate">{e.subject}</div>
                  </div>
                  <div className="text-xs text-slate-500 whitespace-nowrap text-right shrink-0">
                    {new Date(e.created_at).toLocaleString()}
                  </div>
                </div>

                {expanded === e.id && (
                  <div className="px-6 pb-6 -mt-2">
                    {e.body && (
                      <pre className="whitespace-pre-wrap text-sm text-slate-700 bg-slate-50 border border-slate-100 rounded-md p-4 font-sans">{e.body}</pre>
                    )}
                    {tab === "preview" && (
                      <div className="mt-3 flex items-center gap-2">
                        <button onClick={(ev) => { ev.stopPropagation(); handleContinue(e); }}
                          className="px-3 py-1.5 rounded-md bg-[#0047FF] text-white text-sm font-medium hover:bg-[#003BE3] flex items-center gap-1.5"
                          data-testid={`continue-btn-${e.id}`}>
                          <Pencil className="h-3.5 w-3.5" /> Continue editing
                        </button>
                        <button onClick={(ev) => { ev.stopPropagation(); handleDelete(e.id); }}
                          className="px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 text-sm flex items-center gap-1.5"
                          data-testid={`delete-btn-${e.id}`}>
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </AppShell>
  );
}

function Tab({ active, onClick, icon: Icon, label, count, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition-colors ${
        active ? "border-[#0047FF] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
      }`}>
      <Icon className="h-4 w-4" /> {label}
      <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? "bg-[#0047FF] text-white" : "bg-slate-100 text-slate-600"}`}>{count}</span>
    </button>
  );
}
